import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Calculator, Menu, Moon, Sun, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const LINKS = [
  { label: "Thesis", href: "#thesis" },
  { label: "Parcels", href: "#parcels" },
  { label: "Infrastructure", href: "#infrastructure" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ theme, onToggleTheme, onNavigate, onOpenCalc }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (href) => {
    setMenuOpen(false);
    onNavigate(href);
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md"
      data-testid="navbar"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-10">
        <button
          onClick={() => go("#top")}
          className="flex items-center gap-3"
          data-testid="nav-logo"
        >
          <span className="relative flex h-8 w-8 items-center justify-center border-2 border-foreground">
            <span className="absolute inset-1 bg-accent" />
          </span>
          <span className="font-display text-lg font-extrabold uppercase tracking-tight">
            Dholera<span className="text-accent"> NextEra</span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 md:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/investments"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
            data-testid="nav-link-investments"
          >
            Investments
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={onToggleTheme}
            className="flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-secondary"
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            <motion.span
              key={theme}
              initial={{ rotate: -60, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              transition={{ duration: 0.35 }}
              className="flex"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </motion.span>
          </button>
          <Button
            onClick={onOpenCalc}
            className="hidden h-9 rounded-none px-4 font-mono text-[11px] uppercase tracking-[0.15em] sm:flex"
            data-testid="nav-calculator-btn"
          >
            <Calculator className="mr-2 h-3.5 w-3.5" />
            ROI Engine
          </Button>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-secondary md:hidden"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-border bg-background md:hidden"
            data-testid="mobile-nav-panel"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="py-2.5 text-left font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/investments"
                onClick={() => setMenuOpen(false)}
                className="py-2.5 text-left font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
                data-testid="mobile-nav-link-investments"
              >
                Investments
              </Link>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onOpenCalc();
                }}
                className="py-2.5 text-left font-mono text-xs uppercase tracking-[0.2em] text-accent"
                data-testid="mobile-nav-calculator"
              >
                ROI Engine
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
